import Phaser from 'phaser';
import { ulid } from 'ulid';
import { CONSTANTS, gameState } from '../game.js';
import { EventTypes } from '../events/EventTypes.js';
import { PlayLogger } from '../utils/PlayLogger';

// マネージャークラスのインポート
import { ScoreManager } from '../managers/ScoreManager.js';
import { UIManager } from '../managers/UIManager.js';
import { TargetManager } from '../managers/TargetManager.js';
import { ComboManager } from '../managers/ComboManager.js';
import { GameStateManager } from '../managers/GameStateManager.js';
import { CollisionManager } from '../managers/CollisionManager.js';
import { StretchGaugeManager } from '../managers/StretchGaugeManager.js';
import { TimerManager } from '../managers/TimerManager.js';

// オブジェクトクラスのインポート
import { Weight } from '../objects/Weight.js';
import { RubberBand } from '../objects/RubberBand.js';

export class GameScene extends Phaser.Scene {
    constructor() {
        super({ key: 'GameScene' });
        
        // BGM
        this.bgm = null;
        
        // マネージャーの初期化
        this.uiManager = null;
        this.targetManager = null;
        this.gameStateManager = null;
        this.collisionManager = null;
        this.comboManager = null;
        this.stretchGaugeManager = null;
        this.timerManager = null;
        
        // ゲームオブジェクト
        this.rubberBand = null;
        this.weight = null;
        this.anchor = { x: 0, y: 0 };
        
        // ユーティリティ
        this.scoreManager = null;
        
        // 背景
        this.brickBackground = null;
        
        // カーソルポイント
        this.cursorPoint = null;
    }

    create() {
        console.log('GameScene: Create function called');
        
        // デスクトップの場合のみカーソルを非表示に
        if (!this.game.device.input.touch) {
            this.input.setDefaultCursor('none');
        }
        
        // 前回のゲームオブジェクトをクリーンアップ
        this.cleanup();
        
        // 背景色の設定
        this.cameras.main.setBackgroundColor('#87CEEB');
        
        // レンガの背景を作成
        this.createBrickBackground();
        
        // 物理エンジンの設定はgame.jsで行うため、ここでは不要
        
        // 初期位置の設定
        this.anchor = {
            x: this.game.config.width / 2,
            y: this.game.config.height / 2
        };
        
        // マネージャーの初期化
        this.initializeManagers();
        
        // アンカーポイントの作成（小さな鉄製の見た目）
        // 外側リング
        const outerRing = this.add.circle(0, 0, 8, 0x333333);
        outerRing.setStrokeStyle(1, 0x999999);
        
        // 中間リング
        const middleRing = this.add.circle(0, 0, 5, 0x222222);
        middleRing.setStrokeStyle(1, 0xAAAAAA);
        
        // 中心点
        const centerPoint = this.add.circle(0, 0, 2, 0x111111);
        
        // コンテナにまとめる
        this.cursorPoint = this.add.container(0, 0, [outerRing, middleRing, centerPoint]);
        this.cursorPoint.setDepth(100);
        
        // アンカーポイントを画面中央に配置
        this.cursorPoint.setPosition(this.anchor.x, this.anchor.y);
        
        // ゲーム開始のカウントダウンを開始
        this.startCountdown();
        
        console.log('GameScene: Create function completed');
    }
    
    /**
     * レンガの背景を作成する
     */
    createBrickBackground() {
        // 既存の背景をクリア
        if (this.brickBackground) {
            this.brickBackground.destroy();
        }
        
        // 画面サイズを取得
        const width = this.game.config.width;
        const height = this.game.config.height;
        
        // TileSpriteとして背景を作成（画面より大きめに）
        this.brickBackground = this.add.tileSprite(
            width / 2,    // x位置（中央）
            height / 2,   // y位置（中央）
            width + 100,  // 幅（画面より少し大きく）
            height + 100, // 高さ（画面より少し大きく）
            'brick'       // テクスチャキー
        );
        
        // 背景を最背面に配置
        this.brickBackground.setDepth(-100);
    }
    
    /**
     * 各種マネージャーの初期化
     */
    initializeManagers() {
        // スコアマネージャーの初期化
        this.scoreManager = new ScoreManager(this);
        
        // UIマネージャーの初期化
        this.uiManager = new UIManager(this);
        
        // StretchGaugeマネージャーの初期化
        this.stretchGaugeManager = new StretchGaugeManager(this);
        
        // ゲーム状態マネージャーの初期化
        this.gameStateManager = new GameStateManager(this);

        // ターゲット生成用のSeed値を生成
        const seed = ulid();
        gameState.playState.currentSeed = seed;
        
        // ターゲットマネージャーの初期化（Seed値を渡す）
        this.targetManager = new TargetManager(this, seed);
        
        // プレイ開始ログの記録
        PlayLogger.logPlayStart(seed).then(playId => {
            gameState.playState.playId = playId;
        });

        // コンボマネージャーの初期化
        this.comboManager = new ComboManager(this);
        
        // 衝突マネージャーの初期化
        this.collisionManager = new CollisionManager(this);
        
        // タイマーマネージャーの初期化
        this.timerManager = new TimerManager(this);
    }
    
    /**
     * カウントダウンの開始
     */
    startCountdown() {
        this.uiManager.startCountdown(() => this.startGame());
    }
    
    /**
     * ゲームの開始
     */
    startGame() {
        // BGM設定を確認して再生
        if (gameState.bgmEnabled) {
            this.bgm = this.sound.add('bgm', { loop: true });
            this.bgm.play();
        }
        
        // タイマーの表示を作成
        this.timerManager.createTimerDisplay();
        
        // 画面中央を初期位置として使用
        const centerX = this.game.config.width / 2;
        const centerY = this.game.config.height / 2;
        this.anchor.x = centerX;
        this.anchor.y = centerY;

        // 錘の作成（画面中央を初期位置として使用、コールバック関数を渡す）
        this.weight = new Weight(this, centerX, centerY, {
            onBodyUpdated: (oldBody, newBody) => {
                if (this.rubberBand && !this.rubberBand.isBroken()) {
                    const weightInstance = {
                        getBody: () => newBody
                    };
                    this.rubberBand.updateWeightBody(oldBody, weightInstance);
                }
            }
        });
        
        // ゴム紐の作成（コールバック関数を渡す）
        this.rubberBand = new RubberBand(this, this.weight, this.anchor, {
            onNaturalLengthChanged: (naturalLength, maxLength) => {
                if (this.stretchGaugeManager) {
                    this.stretchGaugeManager.updateMaxLength(maxLength);
                    // console.log(`ゴム紐の自然長が変更されました。新しい自然長: ${naturalLength}, 最大長さ: ${maxLength}`);
                }
            },
            onBroken: () => this.gameOver(),
            // クリティカル状態に入った時のコールバック
            onEnterCritical: () => {
                // 制限時間を1秒短くする
                this.timerManager.reduceTime(1);
                // タイマー速度を10倍にする
                this.timerManager.setTimerSpeed(10);
                // console.log('クリティカル状態に入りました。制限時間が1秒短くなり、タイマーが10倍速になりました。');
            },
            // クリティカル状態から回復した時のコールバック
            onRecoverFromCritical: () => {
                // タイマー速度を元に戻す
                this.timerManager.setTimerSpeed(1);
                // console.log('クリティカル状態から回復しました。タイマー速度が通常に戻りました。');
            }
        });
        
        // タイマー終了イベントのリスナー登録
        this.events.on(EventTypes.TIMER.TIMEUP, this.onTimeUp, this);
        
        // 的の作成（アクティブ1つ + 予告2つ = 合計3つ）
        this.targetManager.createTargets(3);
        
        // スコアマネージャーのセットアップ
        this.scoreManager.setup(this.rubberBand);
        
        // 衝突マネージャーの初期化（コールバック関数を渡す）
        this.collisionManager = new CollisionManager(this, {
            onWeightTargetCollision: this.handleWeightTargetCollision.bind(this)
        });
        
        // 衝突マネージャーのセットアップ
        this.collisionManager.setup();
        
        // ポインターイベントリスナーの登録
        this.pointerMoveHandler = this.handlePointerMove.bind(this);
        this.input.on('pointerdown', this.pointerMoveHandler);
        this.input.on('pointermove', this.pointerMoveHandler);
        
        // 動くターゲット出現時のイベントリスナー（シンプルなのでインラインのアロー関数を使用）
        this.events.on(EventTypes.TARGET.MOVEMENT_START, () => {
            console.log('動的ターゲットが出現しました');
        }, this);
        
        // ゴム紐ゲージの作成（初期の最大長さを渡す）
        this.stretchGaugeManager.createStretchGauge(this.rubberBand.getMaxLength());
        
        // FPSカウンターの作成（デバッグ用）
        this.uiManager.createFpsCounter();
        
        // リビジョン情報の表示
        this.uiManager.createRevisionText();
        
        // ゲーム開始
        this.gameStateManager.startGame();
        this.timerManager.startTimer();
    }
    
    update() {
        // ゲームが開始されていない場合は何もしない
        if (!this.gameStateManager.isStarted()) {
            return;
        }

        // 錘の更新
        if (this.weight) {
            this.weight.update();
        }
       
        // ゲームオーバーの時は重りだけ動かす
        if (this.gameStateManager.isOver()) {
            return;
        }

        // ゴム紐の更新
        if (this.rubberBand) {
            const isBroken = this.rubberBand.update();
            
            // ゴム紐が切れた場合
            if (isBroken) {
                this.gameOver();
            }
            
            // ゴム紐の長さを計算してゲージを更新
            if (this.weight && this.anchor) {
                const weightPos = this.weight.getPosition();
                if (weightPos) {
                    const dx = this.anchor.x - weightPos.x;
                    const dy = this.anchor.y - weightPos.y;
                    const currentLength = Math.sqrt(dx * dx + dy * dy);
                    const maxLength = this.rubberBand.getMaxLength();
                    
                    // 自然長も取得
                    const naturalLength = this.rubberBand.getNaturalLength();
                    
                    // ゲージの更新（自然長も渡す）
                    this.stretchGaugeManager.updateStretchGauge(currentLength, maxLength, naturalLength);
                    
                    // パーフェクト状態の判定
                    this.checkPerfectState();
                }
            }
        }
        
        // コンボマネージャーの更新
        if (this.comboManager) {
            this.comboManager.update();
        }
        
        // FPSカウンターの更新
        this.uiManager.updateFpsCounter();
    }
    
    /**
     * パーフェクト状態をチェックして重りに通知
     */
    checkPerfectState() {
        if (!this.rubberBand || !this.weight) return;
        
        // 伸び率を取得
        const stretchRatio = this.rubberBand.getStretchRatio();
        
        // パーフェクト状態かどうかを判定
        const isPerfect = stretchRatio >= CONSTANTS.STRETCH_EVALUATION.PERFECT.min;
        
        // 重りにパーフェクト状態を通知
        this.weight.setPerfectState(isPerfect);
    }
    
    /**
     * ゲームオーバー処理
     */
    gameOver() {
        if(this.gameStateManager.isOver()){
            return;
        }
        // BGMの停止
        if (this.bgm) {
            this.bgm.stop();
            this.bgm.destroy();
            this.bgm = null;
        }
        
        // タイマーの停止
        if (this.timerManager) {
            this.timerManager.stopTimer();
        }

        // 最大コンボ数を保存
        if (this.comboManager) {
            gameState.playState.maxComboCount = this.comboManager.getMaxComboCount();
            console.log(`最大コンボ数: ${gameState.playState.maxComboCount}`);
        }
        
        // プレイ時間を保存
        if (this.timerManager) {
            gameState.playState.playTime = Math.round(this.timerManager.getElapsedTime());
            console.log(`プレイ時間: ${gameState.playState.playTime}秒`);
        }

        this.gameStateManager.gameOver(this.scoreManager);

        this.time.delayedCall(1000, () => {
            this.scene.start('GameOverScene');
        });
    }
    
    /**
     * 錘と的の衝突時の処理
     * @param {Matter.Body} targetBody - 的の物理ボディ
     */
    handleWeightTargetCollision(targetBody) {
        if (this.gameStateManager.isOver()) {
            return;
        }
        const target = this.targetManager.findTarget(targetBody);
        const comboCount = this.comboManager.incrementCombo();
        
        this.scoreManager.calculateAndAddPoints(target, comboCount);
        
        // 評価を計算
        const evaluation = this.scoreManager.evaluateStretch(this.rubberBand.getStretchRatio());
        
        // 評価に応じた時間延長
        const evaluationKey = Object.entries(CONSTANTS.EVALUATION.TEXT)
            .find(([key, value]) => value === evaluation)?.[0];
        
        if (evaluationKey) {
            const extensionTime = CONSTANTS.STRETCH_EVALUATION[evaluationKey].timeExtension;
            this.timerManager.extendTime(extensionTime);
        } else {
            // 評価が見つからない場合はデフォルト値を使用
            this.timerManager.extendTime(CONSTANTS.STRETCH_EVALUATION.PASS.timeExtension);
        }
        // ターゲット破壊エフェクトの表示（評価を渡す）
        target.createHitEffect(evaluation);
        
        // ターゲットタイプに応じた処理
        this.handleTargetTypeEffect(target);
    }
    
    /**
     * ポインター移動時の処理
     * @param {Phaser.Input.Pointer} pointer - ポインターオブジェクト
     */
    handlePointerMove(pointer) {
        // タッチデバイスの場合はポインターが押されている時のみ処理
        if (!this.gameStateManager.isOver() && (!this.game.device.input.touch || pointer.isDown)) {
            const scaledX = pointer.x / this.game.scale.zoom;
            const scaledY = pointer.y / this.game.scale.zoom;
            
            this.anchor.x = scaledX;
            this.anchor.y = scaledY;
            this.rubberBand.setAnchorPosition(scaledX, scaledY);
            
            if (this.cursorPoint) {
                this.cursorPoint.x = scaledX;
                this.cursorPoint.y = scaledY;
            }
        }
    }
    
    /**
     * ターゲット衝突後の処理
     * @param {AbstractTarget} target - ターゲットオブジェクト
     */
    handleTargetTypeEffect(target) {
        // 次のターゲットをアクティブにする
        this.targetManager.activateNextTarget();
        this.targetManager.updatePredictiveTargetsOrder();
    }
    
    /**
     * タイムアップ時の処理
     */
    onTimeUp() {
        if (this.rubberBand && !this.rubberBand.isBroken()) {
            this.timerManager.showTimeUpEffect();
            this.rubberBand.break();
            this.gameOver();
        }
    }
    
    /**
     * リソースのクリーンアップ
     */
    cleanup() {
        console.log('GameScene: Cleaning up previous game objects');
        
        // BGMの停止
        if (this.bgm) {
            this.bgm.stop();
            this.bgm.destroy();
            this.bgm = null;
        }
        
        // イベントリスナーの削除
        this.events.off(EventTypes.TIMER.TIMEUP, this.onTimeUp, this);
        this.events.off(EventTypes.TARGET.MOVEMENT_START);
        
        // ポインターイベントリスナーの削除
        if (this.pointerMoveHandler) {
            this.input.off('pointermove', this.pointerMoveHandler);
            this.input.off('pointerdown', this.pointerMoveHandler);
            this.pointerMoveHandler = null;
        }
        
        // 各マネージャーのクリーンアップ
        if (this.uiManager) this.uiManager.cleanup();
        if (this.stretchGaugeManager) this.stretchGaugeManager.cleanup();
        if (this.targetManager) this.targetManager.cleanup();
        if (this.gameStateManager) this.gameStateManager.cleanup();
        if (this.collisionManager) this.collisionManager.cleanup();
        if (this.comboManager) this.comboManager.cleanup();
        if (this.timerManager) this.timerManager.cleanup();
        
        // 錘のクリーンアップ
        if (this.weight && typeof this.weight.destroy === 'function') {
            this.weight.destroy();
            this.weight = null;
        }
        
        // ゴム紐のクリーンアップ
        if (this.rubberBand && typeof this.rubberBand.destroy === 'function') {
            this.rubberBand.destroy();
            this.rubberBand = null;
        }
        
        // 背景のクリーンアップ
        if (this.brickBackground && typeof this.brickBackground.destroy === 'function') {
            this.brickBackground.destroy();
            this.brickBackground = null;
        }
        
        // カーソルポイントのクリーンアップ
        if (this.cursorPoint) {
            this.cursorPoint.destroy();
            this.cursorPoint = null;
        }
    }
}
